import { getRolesByRoleId } from "../modules/settingsManager/services/UserService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "./errorCodes";

const COMPONENT_NAME = "Admin Utilities/Permissions";

const PROMPTS_MANAGEMENT_PROMPTS_PAGE = "SiNzX";
const PROMPTS_MANAGEMENT_CONFIG_PAGE = "2J0Ca";
const AGENT_CONSOLE_PAGE = "hiW1e";
const UPDATE_CONTACT_PAGE = "1Tcgj";
const DISPOSITION_MANAGEMENT_PAGE = "1mkif";
const CANNED_RESPONSES_PAGE = "0lgwz";
const EXTERNAL_CONTACTS_PAGE = "670jf";
const STATUS_ALERTS_PAGE = "crtj9";
const WEB_CHAT_PAGE = "wr9xz";
const CALL_ME_NOW_PAGE = "qtp8x";
const TRANSLATION_TERMINOLOGY_PAGE = "2pvm9";
const LEX_BOTS_PAGE = "8qfuy";
const SETTINGS_AGENT_CONSOLE_PAGE = "frvu9";
const REPORTING_CONFIGURATION_PAGE = "o1kzv";
const AGENT_EXTENSIONS_PAGE = "2ujd8";
const USER_MANAGEMENT_PAGE = "y6ij5";
const INTEGRATIONS_PAGE = "ai1ue";
const QUEUES_PAGE = "gau5z";
const AMAZON_CONSOLE_PAGE = "qjx00";
const ALL_DASHBOARDS = "a5du9";

export const ROLE_PERMISSION_GROUPS = [
	{
		label: "Prompts Management",
		id: "PromptsManagement",
		permissions: [
			{
				id: PROMPTS_MANAGEMENT_CONFIG_PAGE,
				label: "Configuration",
			},
			{
				id: PROMPTS_MANAGEMENT_PROMPTS_PAGE,
				label: "Prompts",
			},
		],
	},
	{
		label: "Agent Console",
		id: "AgentConsole",
		permissions: [
			{
				id: AGENT_CONSOLE_PAGE,
				label: "Agent Console",
			},
			{
				id: UPDATE_CONTACT_PAGE,
				label: "Update Contact",
			},
		],
	},
	{
		label: "Handling",
		id: "Handling",
		permissions: [
			{
				id: DISPOSITION_MANAGEMENT_PAGE,
				label: "Disposition Management",
			},
			{
				id: CANNED_RESPONSES_PAGE,
				label: "Canned Responses",
			},
			{
				id: EXTERNAL_CONTACTS_PAGE,
				label: "External Contacts",
			},
			{
				id: STATUS_ALERTS_PAGE,
				label: "Status Alerts",
			},
		],
	},
	{
		label: "Widget Management",
		id: "WidgetManagement",
		permissions: [
			{
				id: WEB_CHAT_PAGE,
				label: "Web Chat",
			},
			{
				id: CALL_ME_NOW_PAGE,
				label: "Call Me Now",
			},
			{
				id: TRANSLATION_TERMINOLOGY_PAGE,
				label: "Translation Terminology",
			},
		],
	},
	{
		label: "Lex Bots Management",
		id: "LexBotManagement",
		permissions: [
			{
				id: LEX_BOTS_PAGE,
				label: "Lex Bots",
			},
		],
	},
	{
		label: "Settings",
		id: "Settings",
		permissions: [
			{
				id: SETTINGS_AGENT_CONSOLE_PAGE,
				label: "Agent Console",
			},
			{
				id: REPORTING_CONFIGURATION_PAGE,
				label: "Reporting Configuration",
			},
			{
				id: AGENT_EXTENSIONS_PAGE,
				label: "Agent Extensions",
			},
			{
				id: USER_MANAGEMENT_PAGE,
				label: "User Management",
			},
			{
				id: INTEGRATIONS_PAGE,
				label: "Integrations",
			},
			{
				id: QUEUES_PAGE,
				label: "Queues",
			},
		],
	},
	{
		label: "Amazon Console",
		id: "AmazonConsole",
		permissions: [
			{
				id: AMAZON_CONSOLE_PAGE,
				label: "Amazon Console",
			},
		],
	},
	{
		label: "Dashboards",
		id: "Dashboards",
		permissions: [],
	},
];

export const PERMISSIONS = {
	PROMPTS_MANAGEMENT_PROMPTS_PAGE,
	PROMPTS_MANAGEMENT_CONFIG_PAGE,
	AGENT_CONSOLE_PAGE,
	UPDATE_CONTACT_PAGE,
	DISPOSITION_MANAGEMENT_PAGE,
	CANNED_RESPONSES_PAGE,
	EXTERNAL_CONTACTS_PAGE,
	STATUS_ALERTS_PAGE,
	WEB_CHAT_PAGE,
	CALL_ME_NOW_PAGE,
	TRANSLATION_TERMINOLOGY_PAGE,
	LEX_BOTS_PAGE,
	SETTINGS_AGENT_CONSOLE_PAGE,
	REPORTING_CONFIGURATION_PAGE,
	AGENT_EXTENSIONS_PAGE,
	USER_MANAGEMENT_PAGE,
	INTEGRATIONS_PAGE,
	QUEUES_PAGE,
	AMAZON_CONSOLE_PAGE,
	ALL_DASHBOARDS,
};

// This object contains all of the permissions that would
//enable a user to use all of a certain modules permissions
// The key is a modules ID
const enableAllPermissions = {
	Dashboards: ALL_DASHBOARDS,
};

let permissionMap = undefined;

export const permissionExists = (permissionId) => {
	if (!permissionMap) {
		permissionMap = {};
		Object.values(PERMISSIONS).forEach((permission) => {
			permissionMap[permission] = true;
		});
	}
	return permissionMap[permissionId];
};

/**
 * Retrieval method for getting a permission ID that would allow a user to use all permissions in a module.
 * @param {String} moduleId The id of the module
 * @returns The string ID of the permission if it exists, otherwise returns undefined
 */
export const getEnableAllPermissionId = (moduleId) => {
	return enableAllPermissions[moduleId];
};

export const DEFAULT_ADMIN_ROLE = {
	roleId: "Admin",
	roleName: "Admin",
	default: true,
	permissionsList: [...Object.values(PERMISSIONS)],
};

export const DEFAULT_AGENT_ROLE = {
	roleId: "Agent",
	roleName: "Agent",
	default: true,
	permissionsList: [AGENT_CONSOLE_PAGE, AMAZON_CONSOLE_PAGE],
};

export const DEFAULT_ROLES = [DEFAULT_ADMIN_ROLE, DEFAULT_AGENT_ROLE];

let userPermissions = undefined;

const mergeUserRoles = (userRoles) => {
	userPermissions = {};
	userRoles.forEach((role) => {
		role.permissionsList.forEach((permission) => {
			userPermissions[permission] = true;
		});
	});
	return userPermissions;
};

export const userHasPermission = (permissionId) => {
	return !!userPermissions && !!userPermissions[permissionId];
};

export const hasAnyPermissions = () => {
	return !!userPermissions && !!Object.keys(userPermissions);
};

export const fetchUserRoles = async (userRoleIds) => {
	try {
		let defaultRoles = [];
		let nonDefaultRoles = [...userRoleIds];
		DEFAULT_ROLES.forEach((defaultRole) => {
			const roleIndex = userRoleIds.findIndex((roleId) => defaultRole.roleId === roleId);
			if (roleIndex > -1) {
				defaultRoles.push(defaultRole);
				nonDefaultRoles.splice(roleIndex, 1);
			}
		});
		const roles =
			nonDefaultRoles.length > 0 ? [...(await getRolesByRoleId(nonDefaultRoles)), ...defaultRoles] : [...defaultRoles];
		mergeUserRoles(roles);
	} catch (ex) {
		v2.error(`Error retrieving user permissions`, {
			additionalData: {
				error: errorToObject(ex),
			},
			logCode: errorCodes.GET_USER_DATA_ERROR,
			componentName: COMPONENT_NAME,
		});
		userPermissions = {};
	}
	return userPermissions;
};
